import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { ScrollArea } from '../ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { useGameStore } from '../../store/gameStore';
import { gameAPI } from '../../services/gameAPI';
import { User, MapPin, Search, FileText, MessageSquare, X, BookOpen, Plus, Trash2, Edit2, Save, HelpCircle, Newspaper } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { NewsModal } from './NewsModal';
import { isInterviewAction, getNormalizedActionType, isLocationAction, hasBeenInterviewed } from '../../utils/actionHelpers';
import {
  NO_EVIDENCE_AT_LOCATION,
  TONE_CLASSES,
  examineCoverage,
  formatExamineCoverage,
  hasNonClueEvidenceAtLocation,
} from '../../utils/effectiveness';
import { findOpeningBriefing } from '../../utils/openingBriefing';
import { TutorialBanner } from '../ui/TutorialBanner';

export const CaseFile = ({ open, onClose, tutorialInfo }) => {
  const {
    messageHistory,
    suspects,
    locations,
    clues,
    documents,
    caseData,
    discovered,
    selectedTheme,
    sessionId,
    consultations,
    newsArticles,
    interrogationDialogues,
    examinations,
    guideCharacter,
    isTutorial
  } = useGameStore();

  const [activeTab, setActiveTab] = useState('dialogues');
  const [notes, setNotes] = useState([]);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [selectedNewsArticle, setSelectedNewsArticle] = useState(null);

  // Helper to find when/where a clue was discovered
  const getClueDiscoveryInfo = (clueId) => {
    // Check examinations first
    if (examinations) {
      for (const [locationId, examList] of Object.entries(examinations)) {
        for (const exam of examList) {
          if (exam.clues_found?.includes(clueId)) {
            const locationName = (locations || []).find(l => l.id === locationId)?.name || locationId;
            return { turn: exam.turn, source: 'examine', location: locationName };
          }
        }
      }
    }
    // Check messageHistory for interrogation reveals
    for (const msg of messageHistory) {
      if (msg.revealedInfo?.new_clues?.includes(clueId)) {
        const suspect = (suspects || []).find(s => s.id === msg.target);
        return { turn: msg.turnNumber, source: 'interrogation', suspect: suspect?.name || msg.target };
      }
    }
    return null;
  };

  // Load notes when opening
  useEffect(() => {
    const loadNotes = async () => {
      if (!open || !sessionId) return;

      try {
        setIsLoadingNotes(true);
        const response = await gameAPI.getNotes(sessionId);
        setNotes(response.notes || []);
      } catch (error) {
        console.error('Failed to load notes:', error);
      } finally {
        setIsLoadingNotes(false);
      }
    };

    loadNotes();
  }, [open, sessionId]);

  // Add a new note
  const handleAddNote = async () => {
    if (!newNoteContent.trim() || !sessionId) return;

    try {
      const response = await gameAPI.addNote(sessionId, newNoteContent.trim());
      setNotes([...notes, response.note]);
      setNewNoteContent('');
      toast({
        title: 'Note added',
        description: 'Your note has been saved to the case file.',
      });
    } catch (error) {
      console.error('Failed to add note:', error);
      toast({
        title: 'Failed to add note',
        variant: 'destructive',
      });
    }
  };

  // Update a note
  const handleUpdateNote = async (noteId) => {
    if (!editingContent.trim() || !sessionId) return;

    try {
      await gameAPI.updateNote(sessionId, noteId, editingContent.trim());
      setNotes(notes.map(note =>
        note.id === noteId ? { ...note, content: editingContent.trim() } : note
      ));
      setEditingNoteId(null);
      setEditingContent('');
    } catch (error) {
      console.error('Failed to update note:', error);
      toast({
        title: 'Failed to update note',
        variant: 'destructive',
      });
    }
  };

  // Delete a note
  const handleDeleteNote = async (noteId) => {
    if (!sessionId) return;

    try {
      await gameAPI.deleteNote(sessionId, noteId);
      setNotes(notes.filter(note => note.id !== noteId));
      toast({
        title: 'Note deleted',
      });
    } catch (error) {
      console.error('Failed to delete note:', error);
      toast({
        title: 'Failed to delete note',
        variant: 'destructive',
      });
    }
  };

  const themeAccent = {
    noir: '#DC143C',
    '90s_nyc': '#FF6B35',
    miami_vice: '#FF1493',
  }[selectedTheme || 'noir'];

  // The found-clue chips under an examination round. Identical in the tutorial
  // and the live game, so the two round layouts share it rather than drifting.
  const renderFoundClues = (exam) => (
    exam.clues_found?.length > 0 ? (
      <div className="mt-2 flex flex-wrap gap-1">
        {exam.clues_found.map(clueId => {
          const clue = (clues || []).find(c => c.id === clueId);
          return clue ? (
            <span key={clueId} className="text-xs px-2 py-0.5 rounded bg-black/30" style={{ color: themeAccent }}>
              {clue.name}
            </span>
          ) : null;
        })}
      </div>
    ) : null
  );

  // Group dialogues by person (includes both interviews and interrogations)
  // Deduplicate by checking for same narrative content
  const dialoguesByPerson = {};
  const seenNarratives = new Set();

  messageHistory.forEach(msg => {
    // Include both 'interview' and 'interrogation' action types (case-insensitive)
    // Dialogue only appears if there's an actual interview in messageHistory
    if (isInterviewAction(msg.action) && msg.target && msg.narrative) {
      // Skip duplicates and placeholder text
      const narrativeKey = `${msg.target}_${msg.narrative.substring(0, 100)}`;
      if (seenNarratives.has(narrativeKey)) return;
      if (msg.narrative === 'You continue your investigation...') return;
      seenNarratives.add(narrativeKey);

      const suspect = (suspects || []).find(s => s.id === msg.target);
      if (suspect) {
        if (!dialoguesByPerson[suspect.name]) {
          dialoguesByPerson[suspect.name] = [];
        }
        const normalizedType = getNormalizedActionType(msg.action);
        dialoguesByPerson[suspect.name].push({
          narrative: msg.narrative,
          timestamp: msg.timestamp || new Date().toISOString(),
          type: normalizedType === 'interrogation' ? 'Interrogation' : 'Interview'
        });
      }
    }
  });

  // Get location narratives - only FIRST travel and FIRST examine per discovered location
  const locationNarratives = {};
  const seenLocationActions = {}; // Track first travel/examine per location
  
  messageHistory.forEach(msg => {
    if (isLocationAction(msg.action) && msg.target && msg.narrative) {
      // Only include locations that are in discovered.locations
      if (!discovered.locations?.includes(msg.target)) return;
      
      // Skip placeholder text
      if (msg.narrative === 'You continue your investigation...') return;
      
      const actionLower = msg.action?.toLowerCase();
      const locationActionKey = `${msg.target}_${actionLower}`;
      
      // Only include FIRST travel and FIRST examine per location
      if (seenLocationActions[locationActionKey]) return;
      seenLocationActions[locationActionKey] = true;

      const location = (locations || []).find(l => l.id === msg.target);
      if (location) {
        if (!locationNarratives[location.name]) {
          locationNarratives[location.name] = {
            description: location.description,
            visits: []
          };
        }
        locationNarratives[location.name].visits.push({
          action: actionLower,
          narrative: msg.narrative,
          timestamp: msg.timestamp || new Date().toISOString()
        });
      }
    }
  });

  // The guide's opening briefing. `CenterPanel` shows it once and the next
  // action overwrites it, so the Case File is the only place it stays
  // readable.
  //
  // Gated on `!isTutorial` because the tutorial DOES have a matching entry:
  // `TutorialPage.jsx:107` sets `messageHistory: []` and `:129` immediately
  // appends `addMessage('opening', guideId, 'Welcome! ...')` at `turnCount` 0.
  // The tutorial is out of scope for P3, so this card must not appear there.
  const openingBriefing = isTutorial ? null : findOpeningBriefing(messageHistory);
  const guideName = guideCharacter?.name || 'Your guide';

  // SF-12: the guide's spoken `initial_briefing` — the terse, first-person
  // "here is the case, go" text. It is a DIFFERENT string from the opening
  // scene above (which is `scripted.briefing` on featured, the opening GM
  // message on pro-gen), and until now its only render site was under the
  // portrait in `LeftPanel.jsx`, where selecting any other character replaces
  // it. Read straight off the store's `guideCharacter`, which selection never
  // touches and `loadSavedGame` restores, so this needs no new persisted
  // state and survives a reload.
  //
  // Same `!isTutorial` gate as the card below: the tutorial is out of scope.
  // `initial_briefing` is the backend key; `initialBriefing` is the camelCase
  // shape some payloads carry (both are read in `LeftPanel.jsx`).
  const rawGuideBriefing = isTutorial
    ? null
    : (guideCharacter?.initial_briefing || guideCharacter?.initialBriefing);
  const guideBriefing = typeof rawGuideBriefing === 'string' && rawGuideBriefing.trim()
    ? rawGuideBriefing
    : null;
  // On a GENERATED case the two briefings are the same prose: the opening GM
  // message is written FROM `guide_character.initial_briefing`
  // (`backend/routes/game.py:3079,3086`), so `messageHistory[0].narrative` and
  // `guideCharacter.initial_briefing` are byte-identical and two cards would
  // print the same paragraph twice. On a FEATURED case they are genuinely
  // different texts — `scripted.briefing` is a third-person opening scene,
  // `initial_briefing` is the guide speaking — so both are worth showing.
  // Compare the text rather than the game type: whatever the source, the same
  // prose is only ever rendered once.
  const showGuideBriefing = Boolean(guideBriefing)
    && guideBriefing.trim() !== (openingBriefing?.narrative || '').trim();
  const hasBriefing = Boolean(openingBriefing) || showGuideBriefing;
  const hasConsultations = (consultations?.length || 0) > 0;

  // Filter discovered items (with defensive checks for undefined)
  const discoveredSuspects = (suspects || []).filter(s => discovered?.suspects?.includes(s.id));
  const discoveredClues = (clues || []).filter(c => discovered?.clues?.includes(c.id));
  const discoveredDocuments = (documents || []).filter(d => discovered?.documents?.includes(d.id));
  const discoveredLocations = (locations || []).filter(l => discovered?.locations?.includes(l.id));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-full h-[100dvh] sm:max-w-4xl sm:h-[85vh] bg-gradient-to-br from-slate-900 to-slate-800 border-2 flex flex-col overflow-hidden" style={{ borderColor: themeAccent }}>
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2 pr-10">
            <FileText className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: themeAccent }} />
            Case File
          </DialogTitle>
        </DialogHeader>

        {/* Tutorial info banner */}
        {tutorialInfo && <TutorialBanner title={tutorialInfo.title} content={tutorialInfo.content} />}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Horizontally scrollable tabs on mobile */}
          <div className="overflow-x-auto scrollbar-none overscroll-x-contain flex-shrink-0 -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList className="inline-flex w-max sm:grid sm:w-full sm:grid-cols-8 bg-black/40 min-w-full">
              <TabsTrigger value="dialogues" className="min-h-touch min-w-[60px] sm:min-w-0 text-gray-400 data-[state=active]:text-white data-[state=active]:bg-white/10 flex-col sm:flex-row gap-0.5 sm:gap-1 px-2 sm:px-3">
                <MessageSquare className="w-4 h-4" />
                <span className="text-[10px] sm:text-sm">Dialogues</span>
              </TabsTrigger>
              <TabsTrigger value="people" className="min-h-touch min-w-[60px] sm:min-w-0 text-gray-400 data-[state=active]:text-white data-[state=active]:bg-white/10 flex-col sm:flex-row gap-0.5 sm:gap-1 px-2 sm:px-3">
                <User className="w-4 h-4" />
                <span className="text-[10px] sm:text-sm">People</span>
              </TabsTrigger>
              <TabsTrigger value="locations" className="min-h-touch min-w-[60px] sm:min-w-0 text-gray-400 data-[state=active]:text-white data-[state=active]:bg-white/10 flex-col sm:flex-row gap-0.5 sm:gap-1 px-2 sm:px-3">
                <MapPin className="w-4 h-4" />
                <span className="text-[10px] sm:text-sm">Locations</span>
              </TabsTrigger>
              <TabsTrigger value="clues" className="min-h-touch min-w-[60px] sm:min-w-0 text-gray-400 data-[state=active]:text-white data-[state=active]:bg-white/10 flex-col sm:flex-row gap-0.5 sm:gap-1 px-2 sm:px-3">
                <Search className="w-4 h-4" />
                <span className="text-[10px] sm:text-sm">Clues</span>
              </TabsTrigger>
              <TabsTrigger value="documents" className="min-h-touch min-w-[60px] sm:min-w-0 text-gray-400 data-[state=active]:text-white data-[state=active]:bg-white/10 flex-col sm:flex-row gap-0.5 sm:gap-1 px-2 sm:px-3">
                <FileText className="w-4 h-4" />
                <span className="text-[10px] sm:text-sm">Docs</span>
              </TabsTrigger>
              <TabsTrigger value="news" className="min-h-touch min-w-[60px] sm:min-w-0 text-gray-400 data-[state=active]:text-white data-[state=active]:bg-white/10 flex-col sm:flex-row gap-0.5 sm:gap-1 px-2 sm:px-3">
                <Newspaper className="w-4 h-4" />
                <span className="text-[10px] sm:text-sm">News</span>
              </TabsTrigger>
              <TabsTrigger value="consultations" className="min-h-touch min-w-[60px] sm:min-w-0 text-gray-400 data-[state=active]:text-white data-[state=active]:bg-white/10 flex-col sm:flex-row gap-0.5 sm:gap-1 px-2 sm:px-3">
                <HelpCircle className="w-4 h-4" />
                <span className="text-[10px] sm:text-sm">Consults</span>
              </TabsTrigger>
              <TabsTrigger value="notebook" className="min-h-touch min-w-[60px] sm:min-w-0 text-gray-400 data-[state=active]:text-white data-[state=active]:bg-white/10 flex-col sm:flex-row gap-0.5 sm:gap-1 px-2 sm:px-3">
                <BookOpen className="w-4 h-4" />
                <span className="text-[10px] sm:text-sm">Notes</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Dialogues Tab */}
          <TabsContent value="dialogues" className="flex-1 mt-2 sm:mt-4 overflow-hidden">
            <ScrollArea className="h-full pr-2 sm:pr-4">
              {Object.keys(dialoguesByPerson).length === 0 ? (
                <div className="text-center text-gray-400 py-8">
                  No interviews conducted yet
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(dialoguesByPerson).map(([personName, dialogues]) => (
                    <Card key={personName} className="bg-black/30 border-white/10 p-4">
                      <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                        <User className="w-5 h-5" style={{ color: themeAccent }} />
                        {personName}
                      </h3>
                      <div className="space-y-3">
                        {dialogues.map((dialogue, idx) => (
                          <div key={idx} className="border-l-2 pl-3 py-2" style={{ borderColor: themeAccent }}>
                            {dialogue.type && (
                              <span className="text-xs px-2 py-0.5 rounded mb-2 inline-block" style={{ backgroundColor: `${themeAccent}20`, color: themeAccent }}>
                                {dialogue.type}
                              </span>
                            )}
                            <p className="text-sm text-gray-300 whitespace-pre-wrap">{dialogue.narrative}</p>
                            <p className="text-xs text-gray-500 mt-2">
                              {new Date(dialogue.timestamp).toLocaleString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          {/* People Tab */}
          <TabsContent value="people" className="flex-1 mt-2 sm:mt-4 overflow-hidden">
            <ScrollArea className="h-full pr-2 sm:pr-4">
              {!caseData?.victim && discoveredSuspects.length === 0 ? (
                <div className="text-center text-gray-400 py-8">
                  No people discovered yet
                </div>
              ) : (
                <div className="space-y-4">
                {/* Victim */}
                {caseData?.victim && (
                  <Card className="bg-black/30 border-red-500/50 p-4">
                    <div className="flex gap-4">
                      <img
                        src={caseData.victim.portrait || 'https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=200&h=200&fit=crop'}
                        alt={caseData.victim.name}
                        className="w-20 h-20 rounded-full object-cover border-2 border-red-500"
                      />
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-white">{caseData.victim.name}</h3>
                        <p className="text-sm text-red-400">VICTIM - {caseData.victim.occupation}</p>
                        <p className="text-xs text-gray-400 mt-1">Age: {caseData.victim.age}</p>
                        <p className="text-sm text-gray-300 mt-2">{caseData.victim.background}</p>
                      </div>
                    </div>
                  </Card>
                )}

                {/* Suspects */}
                {discoveredSuspects.map(suspect => {
                  const interviewed = hasBeenInterviewed(suspect.id, interrogationDialogues, messageHistory);
                  return (
                    <Card key={suspect.id} className="bg-black/30 border-white/10 p-4">
                      <div className="flex gap-4">
                        <img
                          src={suspect.portrait || 'https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=200&h=200&fit=crop'}
                          alt={suspect.name}
                          className="w-20 h-20 rounded-full object-cover"
                        />
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-white">{suspect.name}</h3>
                          <p className="text-sm" style={{ color: themeAccent }}>{suspect.occupation}</p>
                          <p className="text-xs text-gray-400 mt-1">Age: {suspect.age}</p>
                          <div className="mt-2">
                            <p className="text-xs text-gray-500 font-semibold">ALIBI:</p>
                            {interviewed ? (
                              <p className="text-sm text-gray-300">{suspect.alibi}</p>
                            ) : (
                              <p className="text-sm text-gray-500 italic">Interview to learn alibi</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
              )}
            </ScrollArea>
          </TabsContent>

          {/* Locations Tab */}
          <TabsContent value="locations" className="flex-1 mt-2 sm:mt-4 overflow-hidden">
            <ScrollArea className="h-full pr-2 sm:pr-4">
              {discoveredLocations.length === 0 ? (
                <div className="text-center text-gray-400 py-8">
                  No locations visited yet
                </div>
              ) : (
                <div className="space-y-4">
                {discoveredLocations.map((location) => {
                  const locationData = locationNarratives[location.name] || {};
                  const locationExaminations = examinations?.[location.id] || [];
                  // One cumulative coverage figure for the location, not a
                  // per-round ratio: the denominator is constant across rounds
                  // (the clue roster is static) and the numerator is what the
                  // player holds here now.
                  const coverage = examineCoverage({ rounds: locationExaminations });
                  const coverageTone = TONE_CLASSES[coverage?.tone || 'neutral'];
                  // I7 / M-1: the coverage denominator counts CLUES only, so a
                  // location whose evidence is a document (noir `doc_2` at
                  // `institutional`) has no coverage figure. Don't tell the
                  // player there is no evidence there when examine recorded a
                  // document here or they hold a document from here by any
                  // route; show nothing instead — there is no documents
                  // denominator to print.
                  const documentEvidenceHere = hasNonClueEvidenceAtLocation({
                    locationId: location.id,
                    rounds: locationExaminations,
                    documents,
                    discoveredDocumentIds: discovered?.documents,
                  });

                  return (
                    <Card key={location.id} className="bg-black/30 border-white/10 p-4">
                      <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                        <MapPin className="w-5 h-5" style={{ color: themeAccent }} />
                        {location.name}
                      </h3>
                      <p className="text-sm text-gray-400 italic mb-3">{location.description}</p>

                      {/* Examination Results — the tutorial is out of scope
                          for P3 and keeps the pre-P3 per-round display. */}
                      {locationExaminations.length > 0 && (isTutorial ? (
                        <div className="mb-4">
                          <p className="text-xs text-gray-500 font-semibold mb-2">EXAMINATIONS:</p>
                          <div className="space-y-2">
                            {locationExaminations.map((exam, idx) => {
                              const hasEvidence = exam.total_clues_at_location > 0;
                              const effectivenessColor = !hasEvidence ? '#6B7280' :  // gray for no evidence
                                                         exam.effectiveness >= 70 ? '#4CAF50' :
                                                         exam.effectiveness >= 40 ? '#FF9800' : '#f44336';
                              return (
                                <div key={idx} className="bg-black/20 rounded p-3 border-l-4" style={{ borderColor: effectivenessColor }}>
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <Search className="w-4 h-4" style={{ color: themeAccent }} />
                                      <span className="text-xs font-semibold text-gray-300">
                                        {exam.focuses?.map(f =>
                                          f.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                                        ).join(' + ') || 'General Search'}
                                      </span>
                                    </div>
                                    <span className="text-xs font-bold px-2 py-0.5 rounded" style={{
                                      backgroundColor: `${effectivenessColor}20`,
                                      color: effectivenessColor
                                    }}>
                                      {hasEvidence ? `${exam.effectiveness}% effective` : 'N/A'}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-4 text-xs text-gray-400">
                                    <span>Turn {exam.turn}</span>
                                    <span>•</span>
                                    {hasEvidence ? (
                                      <span>{exam.clues_found?.length || 0}/{exam.total_clues_at_location} clues found</span>
                                    ) : (
                                      <span className="text-gray-500 italic">No evidence available at this location</span>
                                    )}
                                    {exam.hints_given > 0 && (
                                      <>
                                        <span>•</span>
                                        <span className="text-yellow-500">{exam.hints_given} hint{exam.hints_given !== 1 ? 's' : ''}</span>
                                      </>
                                    )}
                                  </div>
                                  {renderFoundClues(exam)}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="mb-4">
                          <div className="flex items-center justify-between mb-2 gap-2">
                            <p className="text-xs text-gray-500 font-semibold">EXAMINATIONS:</p>
                            {coverage ? (
                              <span
                                data-testid="location-coverage"
                                className={`text-xs font-bold px-2 py-0.5 rounded ${coverageTone.bg} ${coverageTone.text}`}
                              >
                                {formatExamineCoverage(coverage)}
                              </span>
                            ) : documentEvidenceHere ? null : (
                              <span className="text-xs text-gray-500 italic">{NO_EVIDENCE_AT_LOCATION}</span>
                            )}
                          </div>
                          <div className="space-y-2">
                            {locationExaminations.map((exam, idx) => {
                              const roundClueCount = exam.clues_found?.length || 0;
                              return (
                                <div key={idx} className={`bg-black/20 rounded p-3 border-l-4 ${coverageTone.border}`}>
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <Search className="w-4 h-4" style={{ color: themeAccent }} />
                                      <span className="text-xs font-semibold text-gray-300">
                                        {exam.focuses?.map(f =>
                                          f.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                                        ).join(' + ') || 'General Search'}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-4 text-xs text-gray-400">
                                    <span>Turn {exam.turn}</span>
                                    <span>•</span>
                                    <span>
                                      {roundClueCount} clue{roundClueCount !== 1 ? 's' : ''} found this round
                                    </span>
                                    {exam.hints_given > 0 && (
                                      <>
                                        <span>•</span>
                                        <span className="text-yellow-500">{exam.hints_given} hint{exam.hints_given !== 1 ? 's' : ''}</span>
                                      </>
                                    )}
                                  </div>
                                  {renderFoundClues(exam)}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}

                      {/* Investigation Notes (narratives) */}
                      {locationData.visits?.length > 0 && (
                        <div className="space-y-3">
                          <p className="text-xs text-gray-500 font-semibold">INVESTIGATION NOTES:</p>
                          {locationData.visits.map((visit, idx) => (
                            <div key={idx} className="border-l-2 pl-3 py-2" style={{ borderColor: themeAccent }}>
                              <p className="text-xs font-semibold text-gray-400 uppercase">{visit.action}</p>
                              <p className="text-sm text-gray-300 mt-1 whitespace-pre-wrap">{visit.narrative}</p>
                              <p className="text-xs text-gray-500 mt-2">
                                {new Date(visit.timestamp).toLocaleString()}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Empty state for location with no activity */}
                      {!locationData.visits?.length && !locationExaminations.length && (
                        <p className="text-sm text-gray-500 italic">Location discovered but not yet examined</p>
                      )}
                    </Card>
                  );
                })}
              </div>
              )}
            </ScrollArea>
          </TabsContent>

          {/* Clues Tab */}
          <TabsContent value="clues" className="flex-1 mt-2 sm:mt-4 overflow-hidden">
            <ScrollArea className="h-full pr-2 sm:pr-4">
              {discoveredClues.length === 0 ? (
                <div className="text-center text-gray-400 py-8">
                  No clues discovered yet
                </div>
              ) : (
                <div className="space-y-4">
                {discoveredClues.map(clue => {
                  const locationName = (locations || []).find(l => l.id === clue.location)?.name;
                  const discoveryInfo = getClueDiscoveryInfo(clue.id);
                  return (
                    <Card key={clue.id} className="bg-black/30 border-white/10 p-4">
                      {clue.image && (
                        <img
                          src={clue.image}
                          alt={clue.name}
                          className="w-full h-48 object-cover rounded mb-4"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <div className="flex items-start gap-3">
                        <Search className="w-5 h-5 mt-1" style={{ color: themeAccent }} />
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-white">{clue.name}</h3>
                          <p className="text-sm text-gray-300 mt-2">{clue.description}</p>
                          <div className="flex flex-wrap items-center gap-2 mt-3">
                            <span className="text-xs px-2 py-1 rounded font-semibold" style={{ backgroundColor: `${themeAccent}20`, color: themeAccent }}>
                              {clue.type}
                            </span>
                            {locationName && <span className="text-xs text-gray-500">Found at: {locationName}</span>}
                            {discoveryInfo && (
                              <span className="text-xs text-gray-500">
                                • Turn {discoveryInfo.turn} via {discoveryInfo.source === 'examine' ? 'Examine' : `${discoveryInfo.suspect}`}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
              )}
            </ScrollArea>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="flex-1 mt-2 sm:mt-4 overflow-hidden">
            <ScrollArea className="h-full pr-2 sm:pr-4">
              {discoveredDocuments.length === 0 ? (
                <div className="text-center text-gray-400 py-8">
                  No documents discovered yet
                </div>
              ) : (
                <div className="space-y-4">
                {discoveredDocuments.map(doc => (
                  <Card key={doc.id} className="bg-black/30 border-white/10 p-4">
                    {doc.image && (
                      <img
                        src={doc.image}
                        alt={doc.title}
                        className="w-full h-48 object-cover rounded mb-4"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    <div className="flex items-start gap-3">
                      <FileText className="w-5 h-5 mt-1" style={{ color: themeAccent }} />
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-white">{doc.title}</h3>
                        <p className="text-xs font-semibold mt-1" style={{ color: themeAccent }}>{doc.type}</p>
                        <p className="text-sm text-gray-300 mt-3 whitespace-pre-wrap">{doc.content}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
              )}
            </ScrollArea>
          </TabsContent>

          {/* News Tab */}
          <TabsContent value="news" className="flex-1 mt-2 sm:mt-4 overflow-hidden">
            <ScrollArea className="h-full pr-2 sm:pr-4">
              {!newsArticles || newsArticles.length === 0 ? (
                <div className="text-center text-gray-400 py-8">
                  <Newspaper className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No news articles yet</p>
                  <p className="text-sm mt-2">News articles will appear as you investigate</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {newsArticles.map((article, idx) => (
                    <Card
                      key={idx}
                      className="bg-black/30 border-white/10 p-4 cursor-pointer hover:bg-black/40 transition-colors"
                      onClick={() => setSelectedNewsArticle(article)}
                    >
                      <div className="flex items-start gap-3">
                        {article.image ? (
                          <img
                            src={article.image}
                            alt={article.headline}
                            className="w-24 h-24 object-cover rounded border border-white/10 flex-shrink-0"
                          />
                        ) : (
                          <div className="w-24 h-24 rounded border border-white/10 flex-shrink-0 flex items-center justify-center bg-black/20">
                            <Newspaper className="w-12 h-12" style={{ color: themeAccent, opacity: 0.5 }} />
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-white">{article.headline}</h3>
                          {article.subheadline && (
                            <p className="text-sm text-gray-400 mt-1 italic">{article.subheadline}</p>
                          )}
                          <p className="text-sm text-gray-300 mt-3 line-clamp-2">
                            {article.article}
                          </p>
                          <div className="flex items-center justify-between mt-3">
                            <p className={`text-xs ${article.turn ? 'text-gray-500' : 'invisible'}`}>
                              {article.turn ? `Day ${article.turn} of Investigation` : 'placeholder'}
                            </p>
                            <p className="text-xs" style={{ color: themeAccent }}>
                              Click to read full article
                            </p>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          {/* Consultations Tab */}
          <TabsContent value="consultations" className="flex-1 mt-2 sm:mt-4 overflow-hidden">
            <ScrollArea className="h-full pr-2 sm:pr-4">
              <div className="space-y-4">
                {/* The briefing is NOT a consult, and a tester read it as one.
                    Its own section heading and its own icon say so, in text
                    and in shape — never by colour alone. */}
                {hasBriefing && (
                  <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                    <FileText className="w-5 h-5" style={{ color: themeAccent }} />
                    Briefing
                  </h3>
                )}

                {/* SF-12: the guide's own briefing comes first — it is what
                    they said to you out loud, before the scene-setting below.
                    Renders nothing when the case carries no `initial_briefing`,
                    and nothing when it repeats the opening scene verbatim. */}
                {showGuideBriefing && (
                  <Card data-testid="guide-briefing" className="bg-black/30 border-white/10 p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${themeAccent}30` }}>
                        <FileText className="w-4 h-4" style={{ color: themeAccent }} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2 gap-2">
                          <span className="text-xs font-semibold" style={{ color: themeAccent }}>
                            GUIDE'S BRIEFING
                          </span>
                          <span className="text-xs text-gray-500">{guideName}</span>
                        </div>
                        <p className="text-sm text-gray-300 whitespace-pre-wrap">{guideBriefing}</p>
                      </div>
                    </div>
                  </Card>
                )}

                {/* Always ahead of the consultation list:
                    the empty state below must never hide the briefing. */}
                {openingBriefing && (
                  <Card data-testid="opening-briefing" className="bg-black/30 border-white/10 p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${themeAccent}30` }}>
                        <FileText className="w-4 h-4" style={{ color: themeAccent }} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2 gap-2">
                          <span className="text-xs font-semibold" style={{ color: themeAccent }}>
                            OPENING BRIEFING
                          </span>
                          <span className="text-xs text-gray-500">{guideName}</span>
                        </div>
                        <p className="text-sm text-gray-300 whitespace-pre-wrap">{openingBriefing.narrative}</p>
                      </div>
                    </div>
                  </Card>
                )}

                {/* Shown even with no consultations: it is what makes the
                    empty state below read as "no consultations", rather than
                    as a caption on the briefing card above it. Gated on
                    `!isTutorial` like the briefing cards, so the tutorial's
                    Consults tab renders exactly as it did. */}
                {!isTutorial && (
                  <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2 pt-2">
                    <HelpCircle className="w-5 h-5" style={{ color: themeAccent }} />
                    Consultations
                  </h3>
                )}

                {hasConsultations && (
                  consultations.map((consult, idx) => (
                    <Card key={idx} className="bg-black/30 border-white/10 p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${themeAccent}30` }}>
                          <HelpCircle className="w-4 h-4" style={{ color: themeAccent }} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold" style={{ color: themeAccent }}>
                              CONSULTATION {idx + 1}
                            </span>
                            <span className="text-xs text-gray-500">Turn {consult.turn}</span>
                          </div>
                          <p className="text-sm text-gray-300 whitespace-pre-wrap">{consult.content}</p>
                        </div>
                      </div>
                    </Card>
                  ))
                )}
              </div>

              {/* Outside the list container: an empty consultation list must
                  never suppress the briefing card above it. */}
              {!hasConsultations && (
                <div className="text-center text-gray-400 py-8">
                  <HelpCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No consultations yet</p>
                  <p className="text-sm mt-2">Use "Consult Guide" to get hints from your guide character</p>
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          {/* Notebook Tab */}
          <TabsContent value="notebook" className="flex-1 mt-2 sm:mt-4 overflow-hidden">
            <div className="h-full flex flex-col">
              {/* Add new note */}
              <div className="mb-3 sm:mb-4 flex-shrink-0">
                <Textarea
                  placeholder="Write your notes here... (observations, theories, connections)"
                  value={newNoteContent}
                  onChange={(e) => setNewNoteContent(e.target.value)}
                  className="bg-black/30 border-white/20 text-white resize-none min-h-[60px] sm:min-h-[80px] text-sm sm:text-base"
                />
                <Button
                  onClick={handleAddNote}
                  disabled={!newNoteContent.trim()}
                  className="mt-2 min-h-touch"
                  style={{ backgroundColor: themeAccent }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Note
                </Button>
              </div>

              {/* Notes list */}
              <ScrollArea className="flex-1 pr-2 sm:pr-4">
                {isLoadingNotes ? (
                  <div className="text-center text-gray-400 py-8">
                    Loading notes...
                  </div>
                ) : notes.length === 0 ? (
                  <div className="text-center text-gray-400 py-8">
                    <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No notes yet</p>
                    <p className="text-sm mt-2">Write down your observations and theories</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {notes.map((note) => (
                      <Card key={note.id} className="bg-black/30 border-white/10 p-3">
                        {editingNoteId === note.id ? (
                          /* Editing mode */
                          <div>
                            <Textarea
                              value={editingContent}
                              onChange={(e) => setEditingContent(e.target.value)}
                              className="bg-black/30 border-white/20 text-white resize-none min-h-[60px] mb-2"
                              autoFocus
                            />
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleUpdateNote(note.id)}
                                style={{ backgroundColor: themeAccent }}
                              >
                                <Save className="w-3 h-3 mr-1" />
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setEditingNoteId(null);
                                  setEditingContent('');
                                }}
                                className="border-white/20 text-white hover:bg-white/10"
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          /* Display mode */
                          <div>
                            <p className="text-gray-300 whitespace-pre-wrap text-sm">
                              {note.content}
                            </p>
                            <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/10">
                              <span className="text-xs text-gray-500">
                                Turn {note.turn_number} • {new Date(note.created_at).toLocaleString()}
                              </span>
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setEditingNoteId(note.id);
                                    setEditingContent(note.content);
                                  }}
                                  className="h-7 w-7 p-0 text-gray-400 hover:text-white"
                                >
                                  <Edit2 className="w-3 h-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeleteNote(note.id)}
                                  className="h-7 w-7 p-0 text-gray-400 hover:text-red-400"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </Card>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>

      {/* News Article Modal */}
      {selectedNewsArticle && (
        <NewsModal
          open={!!selectedNewsArticle}
          onClose={() => setSelectedNewsArticle(null)}
          article={selectedNewsArticle}
        />
      )}
    </Dialog>
  );
};
